/**
 * Dynamic URL Path Mappings for External URLs
 *
 * Maps URL codes to their corresponding URL paths with parameter placeholders.
 * Placeholders use {paramName} syntax and are replaced at runtime.
 *
 * Available placeholders:
 * - {storeNumber} - Current store number
 * - {viewMode} - View mode (S = Single, M = Multi)
 * - {isMultiStore} - Multi-store flag (Y = Yes, N = No)
 * - {ibToken} - WebFocus IBToken for authenticated requests
 * - {competitor} - Competitor selection for CPI reports (default: HD-LW_Avg)
 */

/**
 * Default competitor for CPI drilldown URLs.
 */

export const DEFAULT_COMPETITOR = 'HD-LW_Avg';

/**
 * Type for base URL selection
 */
export type DynamicBaseUrlType = 'WFC' | 'WFC2' | 'ACENET' | 'RETAILER';

/**
 * Type for URL field selection - identifies which URL path variant to use
 * Common fields: 'current', 'description', 'button', 'automationurl'
 * Custom fields can be defined per URL code (e.g., 'sku', 'order' for YDND)
 */
export type DynamicUrlField = string;

/**
 * Type for URL parameters that can be passed to buildDynamicUrl
 */
export interface DynamicUrlParams {
  storeNumber?: string;
  viewMode?: string;
  isMultiStore?: string;
  ibToken?: string;
  competitor?: string;
  [key: string]: string | undefined;
}

// ============================================================================
// URL FRAGMENT CONSTANTS - Common URL fragments to eliminate repetition.
// Change these in ONE place to update ALL dependent URLs.
// ============================================================================

// Operations - Directory Bases
const OPERATIONS_INVENTORY_DRILLDOWNS = 'Operations/Inventory/DrillDowns/';
const OPERATIONS_MARKETING_DRILLDOWNS = 'Operations/Marketing/DrillDowns/';
const OPERATIONS_PEOPLE_DRILLDOWNS = 'Operations/People/DrillDowns/';
const OPERATIONS_B2B_BASE = 'Operations/B2B/';
const OPERATIONS_B2B_DRILLDOWNS = 'Operations/B2B/drilldowns/';
const OPERATIONS_CPI_DRILLDOWNS = 'Operations/Inventory/DrillDowns/CompetitivePricing/';

// WebFocus - Basic Query Parameters
const LLMAX_WFC_PARAMS = '?IBToken={ibToken}&STORE_FILTER2={storeNumber}';
const BASIC_WFC_PARAMS = '?IBToken={ibToken}&STORE_NUM={storeNumber}';
const STORE_FILTER_WFC_PARAMS = '?IBToken={ibToken}&STORE_FILTER={storeNumber}';


// Operations - Query Parameters
const OPERATIONS_QUERY_PARAMS = `${BASIC_WFC_PARAMS}&OPERATIONS_VIEW={viewMode}`;
const OPERATIONS_B2B_QUERY_PARAMS = `${BASIC_WFC_PARAMS}&OPERATIONS_VIEW={viewMode}`;
const CPI_QUERY_PARAMS = `${OPERATIONS_QUERY_PARAMS}&COMPETITOR_TX={competitor}`;

// Performance - Directory Bases & Query Parameters
const PERFORMANCE_ECOM_DRILLDOWNS = 'Performance/acehardware.com/DrillDowns/';
const PERFORMANCE_QUERY_PARAMS = `${BASIC_WFC_PARAMS}&SALES_VIEW={viewMode}`;
const PERFORMANCE__DRILLDOWNS = 'Performance/DrillDowns/';
const PERFORMANCE_VIEW_LINK_PARAMS = `${BASIC_WFC_PARAMS}&PERFORMANCE_VIEW={viewMode}`;
const PERFORMANCE_HISTORICAL = 'Performance/Historical/';
const PERFORMANCE_HOLISTIC = 'Performance/Holistic/';
const DEFER_PARAM = '&IBIMR_defer=deferred';

// AceNet - Base with placeholders FIRST, then Pg/MopTypeCode appended
const ACENET_RAD_BASE =
  'retail-analytics-dashboard?extraParams=SNo={storeNumber}&extraParams=IsMultiStore={isMultiStore}';
const ACENET_MANGO_BASE =
  'retail-analytics-dashboard-mango-basket/?extraParams={storeNumber}&extraParams={isMultiStore}';

// AWOR Documentation Base
const AWOR_BASE = 'awor/';

/**
 * URL path mappings
 * Key: URL code
 * Value: Object with field types mapping to URL path templates
 */
export const URL_PATHS: Record<string, Partial<Record<DynamicUrlField, string>>> = {
  // Operations - Inventory
  DA: {
    current: `${OPERATIONS_INVENTORY_DRILLDOWNS}rad_inventory_discovery_acceptance_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  BBPP: {
    current: `${OPERATIONS_INVENTORY_DRILLDOWNS}BestBrands/bestbrands_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  MGMA: {
    current: ACENET_MANGO_BASE,
  },
  CRPC: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=CriticalPriceChange`,
    description:
      'ACENET/Documents/AWOR/Marketing%20and%20Promotions/Critical%20Retail%20Pricing_Quick%20Start%20Guide.pdf',
  },
  INST: {
    current: `${OPERATIONS_INVENTORY_DRILLDOWNS}rad_inventory_in_stock_category_drill.fex${OPERATIONS_QUERY_PARAMS}`,
    description: `${AWOR_BASE}inventory-management/in-stock-position/in-stock-position-workspace`,
  },
  MGIS: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=InStock`,
    automationurl: `${ACENET_RAD_BASE}&extraParams=Pg=AutomationReport&extraParams=MopTypeCode=MISO`,
  },
  MOPC: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=AceMop&extraParams=MopTypeCode=AMOP`,
    description: `${AWOR_BASE}ordering/minimum-order-points`,
    automationurl: `${ACENET_RAD_BASE}&extraParams=Pg=AutomationReport&extraParams=MopTypeCode=AMOPAE`,
  },
  MRED: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=AceMop&extraParams=MopTypeCode=MRED`,
    automationurl: `${ACENET_RAD_BASE}&extraParams=Pg=AutomationReport&extraParams=MopTypeCode=AMOPR`,
  },
  MROP: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=AceMop&extraParams=MopTypeCode=ROP`,
  },
  LVL2: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=AceMop&extraParams=MopTypeCode=LVL2`,
  },
  LVL3: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=AceMop&extraParams=MopTypeCode=LVL3`,
  },
  EOQMS: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=MaxStock&extraParams=MopTypeCode=1`,
    description: `${AWOR_BASE}ordering/economic-order-quantities`,
    automationurl: `${ACENET_RAD_BASE}&extraParams=Pg=AutomationReport&extraParams=MopTypeCode=EOQA`,
  },
  EOQMR: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=MaxStock&extraParams=MopTypeCode=3`,
    automationurl: `${ACENET_RAD_BASE}&extraParams=Pg=AutomationReport&extraParams=MopTypeCode=EOQR`,
  },
  MGIT: {
    current: `${OPERATIONS_INVENTORY_DRILLDOWNS}rad_inventory_per_sqft_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  MGOB: {
    current: `${ACENET_RAD_BASE}&extraParams=Pg=CloseOut`,
    description: `${AWOR_BASE}unproductive-inventory/obsolete-inventory`,
  },
  MGOS: {
    description: `${AWOR_BASE}inventory-management/overstock-inventory-store-procedures`,
  },

  // Operations - Marketing
  OPTI: {
    current: 'ace-rewards-maintenance',
  },
  ARW1: {
    current: `${OPERATIONS_MARKETING_DRILLDOWNS}rad_ace_rewards_total_count_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  ARW2: {
    current: `${OPERATIONS_MARKETING_DRILLDOWNS}rad_ace_rewards_active_count_html_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  SO02: {
    current: `${OPERATIONS_MARKETING_DRILLDOWNS}rad_marketing_soci_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  MPTC: {
    description: `marketing-planning-tool`,
  },
  SHRW: {
    current: 'my-store',
  },
  MAPD: {
    current: '01stores/std_reports/storeanalysi/icust005.htm?IBToken={ibToken}',
  },

  // Operations - Fulfillment
  AHF: {
    current: `${OPERATIONS_INVENTORY_DRILLDOWNS}Fulfillment/fulfillment_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },

  // Operations - People
  SFTH: {
    current: `${OPERATIONS_PEOPLE_DRILLDOWNS}training_hours_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  HRAS: {
    current: `${OPERATIONS_PEOPLE_DRILLDOWNS}training_hours_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  BDGC: {
    current: `${OPERATIONS_PEOPLE_DRILLDOWNS}training_hours_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  OATT: {
    current: `${OPERATIONS_PEOPLE_DRILLDOWNS}affinity_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },
  TSRU: {
    current: `${OPERATIONS_PEOPLE_DRILLDOWNS}cmn_ru_summary_drill.fex${OPERATIONS_QUERY_PARAMS}`,
  },

  // Operations - B2B
  BTYG: {
    current: `${OPERATIONS_B2B_BASE}YoY_Growth_New.htm${OPERATIONS_B2B_QUERY_PARAMS}&REPORT_CUSTOMERS=ALL`,
  },
  BTAC: {
    current: `${OPERATIONS_B2B_DRILLDOWNS}rad_b2b_total_customers_drill.fex${OPERATIONS_B2B_QUERY_PARAMS}&REPORT_CUSTOMERS=ALL`,
  },
  BTES: {
    current: `${OPERATIONS_B2B_BASE}YoY_Growth_New.htm${OPERATIONS_B2B_QUERY_PARAMS}&REPORT_CUSTOMERS=EXIST`,
  },
  BTEA: {
    current: `${OPERATIONS_B2B_DRILLDOWNS}rad_b2b_total_customers_drill.fex${OPERATIONS_B2B_QUERY_PARAMS}&REPORT_CUSTOMERS=EXIST`,
  },
  BTNS: {
    current: `${OPERATIONS_B2B_BASE}YoY_Growth_New.htm${OPERATIONS_B2B_QUERY_PARAMS}&REPORT_CUSTOMERS=NEW`,
  },
  BTNA: {
    current: `${OPERATIONS_B2B_DRILLDOWNS}rad_b2b_total_customers_drill.fex${OPERATIONS_B2B_QUERY_PARAMS}&REPORT_CUSTOMERS=NEW`,
  },

  // Performance - Ecommerce (acehardware.com)
  AH08: {
    current: `${PERFORMANCE_ECOM_DRILLDOWNS}rad_ecom_monthly_validated.fex${PERFORMANCE_QUERY_PARAMS}`,
    report: `${PERFORMANCE_ECOM_DRILLDOWNS}2weeks/rad_ecom_validated_detail.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  AH09: {
    current: `${PERFORMANCE_ECOM_DRILLDOWNS}rad_ecom_monthly_cancel_rate.fex${PERFORMANCE_QUERY_PARAMS}`,
    report: `${PERFORMANCE_ECOM_DRILLDOWNS}2weeks/rad_ecom_cancel_rate.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  CCL1: {
    report: `${PERFORMANCE_ECOM_DRILLDOWNS}2weeks/rad_ecom_curbside.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  AH03: {
    report: `${PERFORMANCE_ECOM_DRILLDOWNS}2weeks/rad_ecom_accuracy_drill.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  AMVS: {
    report: `${PERFORMANCE_ECOM_DRILLDOWNS}2weeks/rad_ecom_valiated_matrix.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  YDND: {
    order: `${PERFORMANCE_ECOM_DRILLDOWNS}YardRx/rad_yardrx_drill_by_customer_order.fex${PERFORMANCE_QUERY_PARAMS}`,
    sku: `${PERFORMANCE_ECOM_DRILLDOWNS}YardRx/rad_yardrx_drill_by_sku.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  MRTS: {
    current: `${PERFORMANCE_ECOM_DRILLDOWNS}rad_ecom_territory_skus.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  LLMMPD: {
    current: `Performance/LocalLift/Dashboard/localift.fex${LLMAX_WFC_PARAMS}`,
  },
  YTDS: {
    current: `${PERFORMANCE_ECOM_DRILLDOWNS}rad_product_sync.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  AHOC: {
    current: `${PERFORMANCE_ECOM_DRILLDOWNS}rad_ecom_order_details.fex${PERFORMANCE_QUERY_PARAMS}&ORDERSTATUS_CD=CANCELLED`,
  },
  AHOF: {
    current: `${PERFORMANCE_ECOM_DRILLDOWNS}rad_ecom_order_details.fex${PERFORMANCE_QUERY_PARAMS}&ORDERSTATUS_CD=CLOSEOUT`,
  },

  TRTS: {
    description: `${PERFORMANCE_ECOM_DRILLDOWNS}rad_ecom_transferred_skus.fex${PERFORMANCE_QUERY_PARAMS}`,
  },
  // Performance - View Links (dynamic URLs tied to specific KPIs, with view mode parameter)
  SALES: {
    dept: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_sales_by_dept_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
    merchClass: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_sales_by_mc_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
  },
  GM: {
    dept: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_gross_margin_by_dept_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
    merchClass: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_gross_margin_by_mc_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
  },
  TCNT: {
    dept: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_transactions_by_dept_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
    merchClass: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_transactions_by_mc_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
  },
  GP: {
    dept: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_gross_profit_by_dept_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
    merchClass: `${PERFORMANCE__DRILLDOWNS}YearToDate/rad_gross_profit_by_mc_grid.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
  },

  // Performance - Previous results (historical data links)
  PREV_MO: {
    current: `${PERFORMANCE_HISTORICAL}rad_historical_month_end.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
  },
  PREV_QTR: {
    current: `${PERFORMANCE_HISTORICAL}rad_historical_quarter_end.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
  },
  PREV_YR: {
    current: `${PERFORMANCE_HISTORICAL}rad_historical_year_end.fex${PERFORMANCE_VIEW_LINK_PARAMS}`,
  },

  // Performance - Holistic view links
  STR_YOY_CAL: {
    current: `${PERFORMANCE_HOLISTIC}rad_holistic_store_detail_yoy.fex${BASIC_WFC_PARAMS}`,
  },
  STR_YOY_FISCAL: {
    current: `${PERFORMANCE_HOLISTIC}rad_holistic_store_detail_fiscal.fex${BASIC_WFC_PARAMS}`,
  },
  CHAIN_YOY_CAL: {
    current: `${PERFORMANCE_HOLISTIC}rad_holistic_chain_detail_yoy.fex${BASIC_WFC_PARAMS}`,
  },
  CHAIN_YOY_FISCAL: {
    current: `${PERFORMANCE_HOLISTIC}rad_holistic_chain_detail_fiscal.fex${BASIC_WFC_PARAMS}`,
  },
  CHAIN_STR_YOY_CAL: {
    current: `${PERFORMANCE_HOLISTIC}rad_holistic_chain_stores_calendar.fex${BASIC_WFC_PARAMS}${DEFER_PARAM}`,
  },
  CHAIN_STR_YOY_FISCAL: {
    current: `${PERFORMANCE_HOLISTIC}rad_holistic_chain_stores_fiscal.fex${BASIC_WFC_PARAMS}${DEFER_PARAM}`,
  },
  OVR_CUSTOM_REPORT: {
    current: `${PERFORMANCE_HOLISTIC}Dynamic/Holistic_detail_performance.fex${STORE_FILTER_WFC_PARAMS}`,
  },
  SBF_CUSTOM_REPORT: {
    current: `${PERFORMANCE_HOLISTIC}Dynamic/ecom/Holistic_detail_performance.fex${STORE_FILTER_WFC_PARAMS}`,
  },

  // Button Links (standalone buttons, not tied to specific KPIs)
  OSQA_BTN: {
    button: `rad-legal-disclaimer`,
  },
  MPT_BTN: {
    button: `marketing-planning-tool`,
  },

  // Competitive Pricing Index (CPI) Drill Downs
  CPI_SKU: {
    current: `${OPERATIONS_CPI_DRILLDOWNS}rad_cpi_sku_drill.fex${CPI_QUERY_PARAMS}`,
  },
  CPI_DEPT: {
    current: `${OPERATIONS_CPI_DRILLDOWNS}rad_cpi_dept_drill.fex${CPI_QUERY_PARAMS}`,
  },
  CPI_PROD: {
    current: `${OPERATIONS_CPI_DRILLDOWNS}rad_cpi_product_drill.fex${CPI_QUERY_PARAMS}`,
  },
  CPI_CLASS: {
    current: `${OPERATIONS_CPI_DRILLDOWNS}rad_cpi_merchandise_class_drill.fex${CPI_QUERY_PARAMS}`,
  },
  CPI_GROUP: {
    current: `${OPERATIONS_CPI_DRILLDOWNS}rad_cpi_product_group_drill.fex${CPI_QUERY_PARAMS}`,
  },
  CPI_VENDOR: {
    current: `${OPERATIONS_CPI_DRILLDOWNS}rad_cpi_vendor_drill.fex${CPI_QUERY_PARAMS}`,
  },

  // ADW Reports (AceNet redirect to WebFocus with authentication)
  ADW_REPORTS: {
    current: 'adw-reports',
  },
  ADW_DASHBOARDS: {
    current: 'ADWExtDashboard?portalCode=STO',    //STO - Store Portal (ACENET)
  },

  // My Business Plan
  RGGP: {
    current: '?storesession={storeNumber}&returnUrl=GrowthGoals/',
  },

  // User Guide
  USER_GUIDE: {
    current: 'help/retail-analytics-dashboard',
  },
};
